import { defineStore } from 'pinia'
import { computed } from 'vue'

import {
  TimesheetRegistrationDurationUnit,
  TimesheetRegistrationInput,
  TimesheetRegistrationType,
} from '@/gql/graphql'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import { useTimesheetLinesStore } from '@/components/Timesheet/store/useTimesheetLinesStore'
import { DateEntryLine } from '@/components/Timesheet/types'

export const useFormattedTimesheetRegistrationsStore = defineStore(
  'formatted-timesheet-registrations-store',
  () => {
    const timesheetFormStore = useTimesheetFormStore()
    const timesheetLinesStore = useTimesheetLinesStore()

    const registrations = computed(() => {
      const timesheetRegistrations: TimesheetRegistrationInput[] = []

      // Go through each day on the timesheet
      Object.keys(timesheetFormStore.tableDateEntries).forEach((dateStringKey) => {
        const dayObject = timesheetFormStore.tableDateEntries[dateStringKey]
        if (
          !dayObject.startTime &&
          !dayObject.endTime &&
          !dayObject.comments &&
          dayObject.duration === 0 &&
          dayObject.break === 0
        ) {
          return
        }

        // Find the lines on that day
        const dayLines = dayObject.lines as Record<string, DateEntryLine>

        if (dayLines) {
          Object.entries(dayLines).forEach(([lineKey, lineValue]: [string, DateEntryLine]) => {
            if (!lineValue.duration) {
              return
            }

            const matchingLine = timesheetLinesStore.timesheetLines.find(
              (timesheetLine) => timesheetLine.title === lineKey,
            )

            if (!matchingLine) {
              return
            }

            timesheetRegistrations.push({
              date: dateStringKey,
              unit: dayObject.unit,
              type: TimesheetRegistrationType.Line,
              duration: lineValue.duration,
              isBillable: matchingLine.isPayable,
              customFieldValues: matchingLine.customFieldValues,
            })
          })
        }

        const durationOfNonBillableTimesheetRegistrations =
          timesheetRegistrations.reduce((acc, registration) => {
            if (registration.date === dateStringKey && !registration.isBillable) {
              // eslint-disable-next-line no-param-reassign
              acc += registration.duration
            }
            return acc
          }, 0) || 0

        const dayRegistrationProperties: TimesheetRegistrationInput = {
          date: dateStringKey,
          unit: dayObject.unit,
          type: TimesheetRegistrationType.Day,
          duration: 0,
        }
        if (dayObject.startTime) {
          dayRegistrationProperties.startTime = dayObject.startTime
        }
        if (dayObject.endTime) {
          dayRegistrationProperties.endTime = dayObject.endTime
        }
        if (dayObject.comments) {
          dayRegistrationProperties.comments = dayObject.comments
        }

        const dayRegistrationPayable = {
          ...dayRegistrationProperties,
          isBillable: true,
          duration: dayObject.duration - durationOfNonBillableTimesheetRegistrations || 0,
        }
        timesheetRegistrations.push(dayRegistrationPayable)

        if (durationOfNonBillableTimesheetRegistrations) {
          const dayRegistrationNonPayable = {
            ...dayRegistrationProperties,
            isBillable: false,
            duration: durationOfNonBillableTimesheetRegistrations,
          }
          timesheetRegistrations.push(dayRegistrationNonPayable)
        }

        if (dayObject.startTime && dayObject.endTime && dayObject.break > 0) {
          timesheetRegistrations.push({
            date: dateStringKey,
            unit: TimesheetRegistrationDurationUnit.Hours,
            type: TimesheetRegistrationType.Break,
            duration: dayObject.break || 0,
            isBillable: false,
          })
        }
      })
      return timesheetRegistrations
    })
    return {
      registrations,
    }
  },
)
